import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from "@material-ui/core";
import { Close as CloseIcon, PersonAdd as PersonAddIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import useContactByPhone from "hooks/contacts/useContactByPhone";
import { usePropertyTypes } from "hooks/usePropertyTypes";
import { EditContactDialog } from "../contacts/modal/EditContactDialog";
import { ViewContactDialog } from "../contacts/table/rows/ViewContactDialog";
import { getFavoritesListingsQuery } from "utils/Haim/firestoreUtils";
import { pickMinisitePage } from "utils/minisite/minisitePage";
import { formatPhoneLocal } from "../../../services/signatureFormatters";
import { SYSTEM_CONTACT_ORIGINS } from "../../../@types/sharedSchema";
import type {
  FavoriteListingsOfContact,
  RealEstateContact
} from "../../../@types/sharedSchema";

// Click-handler dialog for the bordered "contact widget" pattern (avatar +
// name + phone, hover-bordered) used across the bulk messaging log,
// signatures sent list, and the signature detail panel.
//
// Behaviour:
//   - If the phone is a contact in the realtor's own book → open the
//     read-only `ViewContactDialog` so the realtor sees the same tabbed
//     contact-detail view they get from the contacts page (general,
//     timeline, sent messages, calls, signatures, favorites). An "Edit"
//     button inside that view escalates to the editable
//     `EditContactDialog`.
//   - If it isn't → show a small "not a contact yet" prompt with
//     an "Add as contact" button that opens `EditContactDialog` in create
//     mode with the phone (and optional name) pre-filled.
//   - Until the lookup has answered → a small loading dialog. Never the
//     prompt: "not answered yet" is not "not a contact".
//
// Centralising this here means the three call-sites (bulk log, sent
// signatures list, signature detail panel) don't repeat the find-or-add
// branching, and a future change to "what happens when you click a
// contact widget" only touches one file.

type ContactWidgetDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Phone of the recipient, in any spelling. The contact is looked up under
   *  every document id the number could be stored as (`getPhoneCandidates`),
   *  so storage variants (`+972...` vs `972...` vs `0...`) all collide. */
  phone: string;
  /** Optional display name to seed the "not a contact" prompt and the
   *  create-mode dialog. The signatures pages have a signer name available
   *  even when the contact doesn't exist yet — using it as the dialog title
   *  is friendlier than showing the bare phone number. */
  nameFallback?: string;
  /** Stacking level for every dialog this widget can open. Defaults to 2100 —
   *  ABOVE the signatures detail side-panel (a hand-rolled fixed overlay at
   *  zIndex 2001) which would otherwise bury the contact view, since MUI
   *  modals default to 1300. Same value ListingPreviewModal uses for the same
   *  reason. */
  zIndex?: number;
};

export default function ContactWidgetDialog({ open, ...rest }: ContactWidgetDialogProps) {
  // Everything this dialog reads — the contact document, its favourite
  // listings — is read by a child that exists only while a widget is open,
  // and is mounted afresh per phone. The hosts keep this component on the
  // page with `phone=""` until one is clicked; nothing should be listening
  // for a dialog nobody has opened, and one phone's dialog state (the edit
  // dialog, the fetched listings) must not carry over to the next.
  if (!open) return null;

  return <OpenContactWidgetDialog key={rest.phone} {...rest} />;
}

function OpenContactWidgetDialog({
  onClose,
  phone,
  nameFallback,
  zIndex = 2100
}: Omit<ContactWidgetDialogProps, "open">) {
  const { t } = useTranslation();
  // The contact's own Firestore document, live — one single-document listener
  // rather than the realtor's whole book downloaded to find one entry in it.
  // It has to be the document and not the lookup layer's `ContactSummary`:
  // this object is handed to `EditContactDialog`, and a form opened on a
  // projection writes the projection back.
  //
  // `exists === undefined` is "not answered yet", NOT "not a contact" — see
  // the loading branch below.
  const { exists, contact: matchedContact, hasError } = useContactByPhone(phone);
  const { propertyTypes } = usePropertyTypes();
  const { user } = useAuth();

  // After the user clicks "Add as contact" from the not-found prompt, flip
  // to the create-mode EditContactDialog. Local state — resets when the
  // outer dialog closes so the next open starts fresh.
  const [creatingNew, setCreatingNew] = useState(false);
  // Edit mode for an existing contact — opened from the Edit button inside
  // the nested-row tabs. While the edit dialog is up the read-only one
  // stays mounted underneath so the realtor returns to it on close.
  const [editingExisting, setEditingExisting] = useState(false);

  // Subscribe to the contact's favorite listings — ViewContactDialog
  // expects this prop and its "Favorite Listings" tab won't load otherwise.
  // Mirrors the subscription in ContactTableRow.tsx.
  const [favoriteListings, setFavoriteListings] =
    useState<FavoriteListingsOfContact | null>(null);
  useEffect(() => {
    if (!matchedContact?.id || !user?.email) {
      setFavoriteListings(null);
      return undefined;
    }
    const unsub = getFavoritesListingsQuery(user.email, matchedContact.id).onSnapshot(
      (snapshot) => {
        setFavoriteListings(pickMinisitePage(snapshot.docs)?.data() ?? null);
      }
    );
    return () => unsub();
  }, [matchedContact?.id, user?.email]);

  const handleClose = () => {
    setCreatingNew(false);
    setEditingExisting(false);
    onClose();
  };

  // Existing contact — render the read-only nested-row dialog. The edit
  // button inside flips us into create/edit dialog mode without closing
  // the outer one (so closing the edit dialog drops back to the view).
  if (matchedContact && !creatingNew) {
    return (
      <>
        <ViewContactDialog
          open
          contact={matchedContact}
          favoriteListingsOfContact={favoriteListings as FavoriteListingsOfContact}
          contactTableAdditionalProps={{
            currentTab: null,
            handleClose
          }}
          handleEditContactDialogOpen={() => setEditingExisting(true)}
          zIndex={zIndex}
        />
        {editingExisting && (
          <EditContactDialog
            open
            onClose={() => setEditingExisting(false)}
            contact={matchedContact}
            propertyTypes={propertyTypes}
            zIndex={zIndex + 1}
          />
        )}
      </>
    );
  }

  // "Add as contact" path — open the create-mode editable dialog with the
  // phone (and optional name) pre-filled. forceCreation kicks the dialog
  // into create flow even though we're handing it a contact-shaped object.
  if (creatingNew) {
    const seedContact: Partial<RealEstateContact> = {
      callerPhoneNumber: phone,
      callerFirstName: nameFallback?.split(" ")[0] ?? "",
      callerLastName: nameFallback?.split(" ").slice(1).join(" ") ?? "",
      contactOrigin: SYSTEM_CONTACT_ORIGINS.bulkMessagingLog
    };
    return (
      <EditContactDialog
        open
        onClose={handleClose}
        contact={seedContact}
        propertyTypes={propertyTypes}
        forceCreation
        zIndex={zIndex}
      />
    );
  }

  // Not answered yet — the listener has not delivered its first snapshot.
  // Claiming "not a contact" now would be a lie about a contact we simply
  // haven't read, and the prompt below leads straight into creating one, so
  // wait instead. A listener that FAILED before ever answering says so rather
  // than spinning for ever; that is still not an answer, so it still offers
  // no "add as contact".
  if (exists === undefined) {
    return (
      <Dialog open onClose={handleClose} fullWidth maxWidth="xs" sx={{ zIndex }}>
        <DialogContent>
          {hasError ? (
            <Stack spacing={0.5} sx={{ py: 2 }}>
              <Typography variant="subtitle2">
                {t("contactsPage.error.failedToGetContacts")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("contactsPage.error.tryRefreshingThePage")}
              </Typography>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">{t("common.loading")}</Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Not a contact yet — small confirmation prompt before sending the
  // realtor into the create flow.
  const displayPhone = formatPhoneLocal(phone) || phone;
  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="xs" sx={{ zIndex }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{t("contactWidget.notAContact.title")}</span>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {nameFallback && (
            <Typography variant="subtitle1" fontWeight="bold">
              {nameFallback}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {displayPhone}
          </Typography>
          <DialogContentText>
            {t("contactWidget.notAContact.body")}
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreatingNew(true)}
        >
          {t("contactWidget.notAContact.addButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
